import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import SectionHeading from 'components/SectionHeading';

/**
 * EPC Section — Graha Prime "Our Project" pattern
 *
 * Бүтэц:
 *   - bg-dark section + bg-secondary-dark inner container (template шиг layered)
 *   - SectionHeading (eyebrow + heading + hr.style-2)
 *   - Filter buttons: EPC 01 / EPC 02 / EPC 03 / EPC 04
 *   - 4 багц ҮРГЭЛЖ харагдана. Товч дарахад тухайн карт онцлогдож бага зэрэг томорно
 *     (шүүхгүй).
 *   - card-overlay: зургийн доороос дээш гарах гарчиг
 */
export default function EpcSection({ epcPackages = [] }) {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');

    // Сонгогдсон багц (highlight). null = юу ч онцлоогүй.
    const [activeKey, setActiveKey] = useState(null);

    if (!epcPackages || epcPackages.length === 0) return null;

    const filters = epcPackages.map((p) => ({
        key: `epc-${p.number}`,
        label: `EPC ${String(p.number).padStart(2, '0')}`,
        number: p.number,
    }));

    // Дахин дарвал онцлолыг болиулна (toggle).
    const handleSelect = (key) =>
        setActiveKey((prev) => (prev === key ? null : key));

    return (
        <section className="py-20 lg:py-28 bg-brand-dark">
            <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
                {/* Inner container — Graha Prime "bg-secondary-dark" layer */}
                <div className="bg-brand-dark-lighter py-12 lg:py-16 px-4 lg:px-8 relative">
                    <SectionHeading
                        variant="dark"
                        eyebrow={lang === 'en' ? 'Our Project' : 'Манай төсөл'}
                        title={
                            lang === 'en'
                                ? 'Construction Packages'
                                : 'Бүтээн байгуулалтын багц'
                        }
                        description={
                            lang === 'en'
                                ? 'The construction is divided into 4 main packages based on facility complexity and engineering schedule.'
                                : 'Газрын тос боловсруулах үйлдвэрийн байгууламжуудын төвөгшил, дэс дараалал, инженерчлэлийн ажилд зарцуулагдах хугацааг харгалзан, бүтээн байгуулалтын ажлыг 4 хэсэгт хуваан хэрэгжүүлж байна.'
                        }
                        align="center"
                    />

                    {/* ── Filter buttons (highlight selector) ── */}
                    <div className="flex flex-wrap gap-3 justify-center mb-10">
                        {filters.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => handleSelect(f.key)}
                                className={`font-heading uppercase font-semibold text-sm tracking-wider px-6 py-2.5 border-2 transition-all duration-300 ${
                                    activeKey === f.key
                                        ? 'bg-brand-yellow text-brand-dark border-brand-yellow'
                                        : 'bg-transparent text-white border-white/20 hover:border-brand-yellow hover:text-brand-yellow'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* ── Бүх багцыг ҮРГЭЛЖ харуулах grid ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {epcPackages.map((epc) => (
                            <EpcCard
                                key={epc.id}
                                epc={epc}
                                lang={lang}
                                t={t}
                                isActive={`epc-${epc.number}` === activeKey}
                                dimmed={
                                    activeKey !== null &&
                                    `epc-${epc.number}` !== activeKey
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Project card — Graha Prime "card-overlay" pattern
 * Зургийн доороос дээш гарах overlay-той карт.
 * isActive — сонгогдсон үед бага зэрэг томорч, шар ring-тэй онцлогдоно.
 */
function EpcCard({ epc, lang, t, isActive = false, dimmed = false }) {
    const title = lang === 'en' && epc.titleEn ? epc.titleEn : epc.title;
    const imageUrl = epc.imageUrl || '/placeholders/epc-default.svg';

    return (
        <Link
            href={`/project/epc#epc-${epc.number}`}
            className={`group relative block w-full transition-all duration-500 origin-center ${
                isActive
                    ? 'lg:scale-105 z-10 ring-2 ring-brand-yellow shadow-2xl'
                    : dimmed
                    ? 'opacity-60 hover:opacity-100'
                    : ''
            }`}
        >
            <div className="relative aspect-[3/4] bg-brand-dark overflow-hidden">
                <Image
                    src={imageUrl}
                    alt={title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Dark overlay always */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/40 to-transparent" />

                {/* Number badge */}
                <div className="absolute top-4 left-4 bg-brand-yellow text-brand-dark font-heading font-bold text-2xl w-14 h-14 flex items-center justify-center">
                    {String(epc.number).padStart(2, '0')}
                </div>

                {/* Bottom overlay — slides up on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-6 transform transition-transform duration-500">
                    <span className="inline-block text-brand-yellow text-xs font-bold uppercase tracking-widest mb-2">
                        EPC {String(epc.number).padStart(2, '0')}
                    </span>
                    <h4 className="font-heading uppercase font-bold text-white text-xl lg:text-2xl leading-tight tracking-tight">
                        {title}
                    </h4>
                </div>
            </div>
        </Link>
    );
}